import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { Database } from '@/../database.types'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Slider } from '@/components/ui/slider'
import { Button } from '@/components/ui/button'
import { X, Save, Plus, Info } from 'lucide-react'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { usePermissions, useUser } from '@/lib/hooks'
import { Alert, AlertDescription } from '@/components/ui/alert'

type TaskAssignmentBlock = Database['public']['Tables']['task_assignment_blocks']['Row']

// Define types for the joined data
type TaskAssignmentWithUser = TaskAssignmentBlock & {
  users?: {
    id: string
    email: string
    first_name: string | null
    last_name: string | null
  } | null
}

type ProjectMember = Database['public']['Tables']['project_members']['Row'] & {
  users?: {
    id: string
    email: string
    first_name: string | null
    last_name: string | null
  } | null
}

interface TaskAssignmentEditorProps {
  taskId: string
  projectId: string
}

export default function TaskAssignmentEditor({ taskId, projectId }: TaskAssignmentEditorProps) {
  const [selectedMemberId, setSelectedMemberId] = useState<string>('')
  const [isAddingNew, setIsAddingNew] = useState(false)
  const queryClient = useQueryClient()
  const permissions = usePermissions()
  const user = useUser()

  // Fetch current active assignment blocks
  const { data: currentAssignments = [] } = useQuery({
    queryKey: ['task-assignment-blocks-current', taskId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('task_assignment_blocks')
        .select(`
          *,
          users!task_assignment_blocks_user_id_fkey (
            id,
            email,
            first_name,
            last_name
          )
        `)
        .eq('task_id', taskId)
        .is('block_end_date', null)
        .order('block_start_date', { ascending: false })

      if (error) throw error
      return data || []
    }
  })

  // Fetch project members
  const { data: projectMembers = [] } = useQuery({
    queryKey: ['project-members', projectId],
    queryFn: async (): Promise<ProjectMember[]> => {
      const { data, error } = await supabase
        .from('project_members')
        .select(`
          *,
          users!project_members_user_id_fkey (
            id,
            email,
            first_name,
            last_name
          )
        `)
        .eq('project_id', projectId)

      if (error) throw error
      return (data || []) as ProjectMember[]
    }
  })

  // Create new assignment block
  const createAssignmentMutation = useMutation({
    mutationFn: async (userId: string) => {
      const { profile } = user
      if (!profile?.id) {
        throw new Error('User not authenticated')
      }
      
      const { error } = await supabase
        .from('task_assignment_blocks')
        .insert({
          task_id: taskId,
          user_id: userId,
          project_id: projectId,
          assigned_by: profile.id,
          block_start_date: new Date().toISOString(),
          assignment_weight: 1.0,
          time_commitment_percentage: 100,
          focus_level: 'normal'
        })

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-assignment-blocks-current', taskId] })
      queryClient.invalidateQueries({ queryKey: ['task-assignment-blocks', taskId] })
      setSelectedMemberId('')
      setIsAddingNew(false)
    }
  })

  // Update assignment block
  const updateAssignmentMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<TaskAssignmentBlock> }) => {
      const { error } = await supabase
        .from('task_assignment_blocks')
        .update(updates)
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-assignment-blocks-current', taskId] })
      queryClient.invalidateQueries({ queryKey: ['task-assignment-blocks', taskId] })
    }
  })

  // End assignment block (remove from current assignments)
  const endAssignmentMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('task_assignment_blocks')
        .update({ block_end_date: new Date().toISOString() })
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-assignment-blocks-current', taskId] })
      queryClient.invalidateQueries({ queryKey: ['task-assignment-blocks', taskId] })
    }
  })

  const availableMembers = projectMembers.filter(
    member => member.user_id && !currentAssignments.some(a => a.user_id === member.user_id)
  )

  // Check permissions
  if (!permissions.canAssignTasks) {
    return (
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Only project admins and owners can manage task assignments.
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <div className="space-y-3">
      {/* Add new assignment section */}
      {isAddingNew ? (
        <div className="flex gap-2">
          <Select value={selectedMemberId} onValueChange={setSelectedMemberId}>
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Select a team member to assign" />
            </SelectTrigger>
            <SelectContent>
              {availableMembers.map((member) => (
                <SelectItem key={member.user_id} value={member.user_id!}>
                  {member.users?.first_name && member.users?.last_name
                    ? `${member.users.first_name} ${member.users.last_name}`
                    : member.users?.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            onClick={() => selectedMemberId && createAssignmentMutation.mutate(selectedMemberId)} 
            disabled={!selectedMemberId || createAssignmentMutation.isPending}
          >
            Assign
          </Button>
          <Button 
            variant="outline"
            onClick={() => {
              setIsAddingNew(false)
              setSelectedMemberId('')
            }}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <Button 
          onClick={() => setIsAddingNew(true)} 
          variant="outline" 
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Team Member
        </Button>
      )}

      {/* Current assignments list */}
      <div className="space-y-2">
        {currentAssignments.length === 0 ? (
          <p className="text-sm text-muted-foreground p-4 text-center border rounded-lg">
            No team members currently assigned.
          </p>
        ) : (
          currentAssignments.map((assignment) => (
            <AssignmentCard
              key={assignment.id}
              assignment={assignment}
              onUpdate={(updates) => updateAssignmentMutation.mutate({ id: assignment.id, updates })}
              onEnd={() => endAssignmentMutation.mutate(assignment.id)}
              isUpdating={updateAssignmentMutation.isPending || endAssignmentMutation.isPending}
              canEdit={permissions.canAssignTasks}
            />
          ))
        )}
      </div>
    </div>
  )
}

// Component for individual assignment display/edit
function AssignmentCard({ 
  assignment, 
  onUpdate, 
  onEnd,
  isUpdating = false,
  canEdit = false
}: {
  assignment: TaskAssignmentWithUser
  onUpdate: (updates: Partial<TaskAssignmentBlock>) => void
  onEnd: () => void
  isUpdating?: boolean
  canEdit?: boolean
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [editedValues, setEditedValues] = useState({
    role_in_task: assignment.role_in_task || 'contributor',
    assignment_weight: assignment.assignment_weight || 1.0,
    time_commitment_percentage: assignment.time_commitment_percentage || 100,
    focus_level: assignment.focus_level || 'normal',
    expected_effort_hours: assignment.expected_effort_hours || 0,
    notes: assignment.notes || ''
  })

  const handleSave = () => {
    onUpdate(editedValues)
    setIsEditing(false)
  }

  const getFocusLevelColor = (level: string) => {
    switch (level) {
      case 'critical': return 'text-red-600'
      case 'high': return 'text-orange-600'
      case 'normal': return 'text-blue-600'
      case 'low': return 'text-gray-600'
      default: return 'text-gray-600'
    }
  }

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'lead': return 'bg-purple-100 text-purple-800'
      case 'reviewer': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-blue-100 text-blue-800'
    }
  }

  if (!isEditing) {
    return (
      <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
        <div className="flex items-center gap-3 flex-1">
          <Avatar className="h-10 w-10">
            <AvatarFallback>
              {assignment.users?.first_name && assignment.users?.last_name
                ? `${assignment.users.first_name} ${assignment.users.last_name}`
                : assignment.users?.email}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium">
                {assignment.users?.first_name && assignment.users?.last_name
                  ? `${assignment.users.first_name} ${assignment.users.last_name}`
                  : assignment.users?.email}
              </span>
              <span className={`text-xs px-2 py-1 rounded-full ${getRoleBadgeColor(assignment.role_in_task || 'contributor')}`}>
                {assignment.role_in_task || 'contributor'}
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
              <span>Weight: {((assignment.assignment_weight || 1) * 100).toFixed(0)}%</span>
              <span>Time: {assignment.time_commitment_percentage || 100}%</span>
              <span className={getFocusLevelColor(assignment.focus_level || 'normal')}>
                {assignment.focus_level || 'normal'} focus
              </span>
              {assignment.expected_effort_hours && (
                <span>{assignment.expected_effort_hours}h expected</span>
              )}
            </div>
            {assignment.notes && (
              <p className="text-sm text-gray-500 mt-1">{assignment.notes}</p>
            )}
          </div>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onEnd}
              disabled={isUpdating}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="p-4 border rounded-lg bg-gray-50">
      <div className="flex items-center gap-3 mb-4">
        <Avatar className="h-10 w-10">
          <AvatarFallback>
            {assignment.users?.first_name && assignment.users?.last_name
              ? `${assignment.users.first_name} ${assignment.users.last_name}`
              : assignment.users?.email}
          </AvatarFallback>
        </Avatar>
        <div>
          <span className="font-medium">
            {assignment.users?.first_name && assignment.users?.last_name
              ? `${assignment.users.first_name} ${assignment.users.last_name}`
              : assignment.users?.email}
          </span>
        </div>
      </div>

      <div className="grid gap-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Role</Label>
            <Select
              value={editedValues.role_in_task}
              onValueChange={(value) => setEditedValues({ ...editedValues, role_in_task: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="lead">Lead</SelectItem>
                <SelectItem value="contributor">Contributor</SelectItem>
                <SelectItem value="reviewer">Reviewer</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Focus Level</Label>
            <Select
              value={editedValues.focus_level}
              onValueChange={(value) => setEditedValues({ ...editedValues, focus_level: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label>Assignment Weight: {(editedValues.assignment_weight * 100).toFixed(0)}%</Label>
          <p className="text-xs text-gray-600 mb-2">
            Relative contribution weight compared to other assignees
          </p>
          <Slider
            value={[editedValues.assignment_weight * 100]}
            onValueChange={(value) => setEditedValues({ ...editedValues, assignment_weight: value[0] / 100 })}
            min={0}
            max={100}
            step={5}
            className="mt-2"
          />
        </div>

        <div>
          <Label>Time Commitment: {editedValues.time_commitment_percentage}%</Label>
          <p className="text-xs text-gray-600 mb-2">
            Percentage of time dedicated to this task
          </p>
          <Slider
            value={[editedValues.time_commitment_percentage]}
            onValueChange={(value) => setEditedValues({ ...editedValues, time_commitment_percentage: value[0] })}
            min={0}
            max={100}
            step={10}
            className="mt-2"
          />
        </div>

        <div>
          <Label>Expected Effort (hours)</Label>
          <Input
            type="number"
            value={editedValues.expected_effort_hours || ''}
            onChange={(e) => setEditedValues({ ...editedValues, expected_effort_hours: parseFloat(e.target.value) || 0 })}
            min={0}
            step={0.5}
          />
        </div>

        <div>
          <Label>Notes</Label>
          <Input
            value={editedValues.notes}
            onChange={(e) => setEditedValues({ ...editedValues, notes: e.target.value })}
            placeholder="Any additional context..."
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setIsEditing(false)
              setEditedValues({
                role_in_task: assignment.role_in_task || 'contributor',
                assignment_weight: assignment.assignment_weight || 1.0,
                time_commitment_percentage: assignment.time_commitment_percentage || 100,
                focus_level: assignment.focus_level || 'normal',
                expected_effort_hours: assignment.expected_effort_hours || 0,
                notes: assignment.notes || ''
              })
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isUpdating}>
            <Save className="h-4 w-4 mr-2" />
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  )
}